// Staged handouts at the table (issue 1154), beside the log: the DM's every one whole, a player's only those sent to
// them — read with the log's first page and moved only by what this person was sent. Nothing here works out who may
// hold a handout: a player's list is what the server sent, and a pin is drawn only when the server sent it.
import { normaliseHandouts, parseHandoutSpec, reduceHandouts, isHandoutBody, type Handout, type PlayerHandout, type ProjectedEvent } from '@dragons-table/vtt-shared';

export type Handouts = Record<string, Handout | PlayerHandout>;

/** The handouts a log page carries: the DM's whole (with `to`), a player's as they read them. */
export function parsePageHandouts(v: unknown): Handouts {
  if (!Array.isArray(v)) return {};
  const out: Handouts = {};
  for (const h of v) {
    const spec = parseHandoutSpec(h);
    if (!spec) continue;
    const raw = h as { to?: unknown; at?: unknown };
    if ('to' in raw) Object.assign(out, normaliseHandouts({ [spec.id]: h }));
    else out[spec.id] = { ...spec, at: typeof raw.at === 'string' ? raw.at : '' };
  }
  return out;
}

/** The handouts moved by one projected event: the DM reduces the body; a player takes the handout sent, or drops the one taken back. */
export function foldHandouts(cur: Handouts, ev: ProjectedEvent): Handouts {
  if (ev.kind === 'dm.event') return isHandoutBody(ev.body) ? reduceHandouts(cur as Record<string, Handout>, ev.body) : cur;
  if (ev.kind === 'handout.set') return { ...cur, [ev.handout.id]: ev.handout };
  if (ev.kind === 'handout.clear' && cur[ev.id]) { const { [ev.id]: _gone, ...rest } = cur; return rest; }
  return cur;
}

/**
 * The DM's handouts as one player holds them, for "View as" (design/07): those sent to everyone or to them, without
 * who else holds them. A player's own list (no `to`) holds nothing to work this out from, so it reads as empty.
 */
export function handoutsAs(handouts: Handouts, playerId: string): Handouts {
  const out: Handouts = {};
  for (const h of Object.values(handouts)) {
    if (!('to' in h) || h.to === null) continue;
    if (h.to !== 'all' && !h.to.includes(playerId)) continue;
    const { to: _to, at, ...spec } = h;
    out[h.id] = { ...spec, at: at ?? '' };
  }
  return out;
}

/** A player's handouts, newest first; the DM's in the order written. */
export const handoutList = (handouts: Handouts, role: string): Array<Handout | PlayerHandout> => {
  const all = Object.values(handouts);
  return role === 'dm' ? all : [...all].sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
};

/** Who holds it, in words, for the DM's list. */
export function audienceWords(h: Handout, nameOf: (id: string) => string): string {
  if (h.to === null) return 'Not sent';
  if (h.to === 'all') return 'Sent to everyone';
  return `Sent to ${h.to.map(nameOf).join(', ')}`;
}

/** The body's paragraphs, as text: split on blank lines, never parsed as markup. */
export const paragraphs = (body: string): string[] => body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
